<template>
  <nav class="admin-nav" :class="{ collapsed }">
    <div class="nav-header">
      <div v-if="!collapsed" class="nav-title">
        <h2>Pose Perfect</h2>
        <span class="nav-subtitle">Admin</span>
      </div>
      <button
        class="nav-toggle"
        type="button"
        :title="collapsed ? 'Expand navigation' : 'Collapse navigation'"
        :aria-label="collapsed ? 'Expand navigation' : 'Collapse navigation'"
        @click="$emit('toggle')"
      >{{ collapsed ? '»' : '«' }}</button>
    </div>

    <div class="nav-menu">
      <RouterLink to="/clients" class="nav-item" title="Clients">
        <span class="nav-icon">👥</span><span class="nav-label">Clients</span>
      </RouterLink>
      <RouterLink to="/galleries" class="nav-item" title="Galleries">
        <span class="nav-icon">📁</span><span class="nav-label">Galleries</span>
      </RouterLink>
      <RouterLink to="/portfolio" class="nav-item" title="Portfolio">
        <span class="nav-icon">🖼️</span><span class="nav-label">Portfolio</span>
      </RouterLink>
      <RouterLink to="/library" class="nav-item" title="Library">
        <span class="nav-icon">📇</span><span class="nav-label">Library</span>
      </RouterLink>
      <RouterLink to="/contact-messages" class="nav-item" title="Messages">
        <span class="nav-icon">📧</span><span class="nav-label">Messages</span>
      </RouterLink>
      <RouterLink to="/settings" class="nav-item" title="Settings">
        <span class="nav-icon">⚙️</span><span class="nav-label">Settings</span>
      </RouterLink>
    </div>

    <div class="nav-footer">
      <a href="https://poseperfect.com" target="_blank" rel="noopener" class="nav-item" title="View Site">
        <span class="nav-icon">🌐</span><span class="nav-label">View Site</span>
      </a>
      <button @click="logout" class="nav-item nav-logout" title="Logout">
        <span class="nav-icon">→</span><span class="nav-label">Logout</span>
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { useAdminApi } from '@/composables/useAdminApi'

defineProps<{ collapsed: boolean }>()
defineEmits<{ (e: 'toggle'): void }>()

const { logout } = useAdminApi()
</script>

<style scoped>
.admin-nav {
  position: fixed;
  left: 0;
  top: 0;
  width: var(--nav-width);
  height: 100vh;
  background: var(--bg-nav);
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  border-right: 1px solid var(--border-default);
  transition: width 0.18s ease;
}

.admin-nav.collapsed { width: var(--nav-width-collapsed); }

.nav-header {
  padding: 1.5rem 1.25rem 1.25rem;
  border-bottom: 1px solid var(--border-default);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.admin-nav.collapsed .nav-header { padding: 1.5rem 0.5rem 1.25rem; justify-content: center; }

.nav-title { min-width: 0; }

.nav-header h2 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--text-primary);
  line-height: 1;
}

.nav-subtitle {
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.nav-toggle {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-surface);
  color: var(--text-secondary);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.95rem;
  line-height: 1;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}
.nav-toggle:hover { color: var(--text-primary); background: var(--bg-surface-hover); border-color: var(--accent); }

.nav-menu { flex: 1; padding: 0.75rem 0; }

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem 1.25rem;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  border-left: 3px solid transparent;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  cursor: pointer;
  background: none;
  border-top: none;
  border-right: none;
  border-bottom: none;
  width: 100%;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
}

.admin-nav.collapsed .nav-item { justify-content: center; padding: 0.7rem 0; gap: 0; }
.admin-nav.collapsed .nav-label { display: none; }

.nav-item:hover {
  color: var(--text-primary);
  background: var(--bg-surface-hover);
}

.router-link-active {
  color: var(--text-primary);
  border-left-color: var(--accent);
  background: var(--accent-dim);
}

.nav-icon { font-size: 1rem; width: 1.25rem; text-align: center; flex-shrink: 0; }

.nav-footer {
  border-top: 1px solid var(--border-default);
  padding: 0.5rem 0;
}

.nav-logout { color: var(--danger-text); }
.nav-logout:hover { color: var(--danger); background: var(--danger-dim); }

@media (max-width: 768px) {
  .admin-nav { width: 100%; height: auto; position: relative; flex-direction: row; flex-wrap: wrap; }
  .admin-nav.collapsed { width: 100%; }
}
</style>
